import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  ChevronRight,
  TriangleAlert,
  Calculator,
  CircleCheck,
  FileSearch,
  FileText,
  Gavel,
  Layers,
  RefreshCcw,
  SendHorizontal,
  Wallet,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { useLayout } from '@/hooks/useMediaQuery';
import { Donut, type DonutSegment } from '@/components/Donut';
import { AGEING_BUCKETS } from '@/lib/ageing';
import {
  Card,
  ErrorState,
  Money,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
} from '@/components/ui';

/**
 * CFO dashboard — PRD §30 and §31.
 *
 * Laid out to answer the §45 questions in order: what have we received, what
 * needs approval, what do we owe, what is ready to pay, and what remains
 * unreconciled.
 */
export function DashboardPage() {
  const { companyId, verticalId, can } = useAuth();
  const money = useMoney();
  const layout = useLayout();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', companyId, verticalId],
    queryFn: () => api.dashboard.summary({ companyId, verticalId }),
  });

  // Shares its key with the payables screen, so whichever loads second is free.
  const { data: ageing } = useQuery({
    queryKey: ['payables', 'ageing', companyId, verticalId],
    queryFn: () => api.payables.ageing({ companyId, verticalId }),
    enabled: can('payable:read'),
  });

  // Names for the vertical breakdown; the summary returns ids, because the
  // aggregation groups on the denormalised field rather than joining.
  const { data: verticals } = useQuery({
    queryKey: ['verticals', 'for-dashboard', companyId],
    queryFn: () => api.settings.verticals({ companyId, pageSize: 200 }),
    enabled: can('vertical:read'),
  });

  const verticalName = (id: string | null) =>
    id ? verticals?.items.find((vertical) => vertical.id === id)?.name : null;

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const segments: DonutSegment[] = AGEING_BUCKETS.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    colour: bucket.colour,
    amount: ageing?.[bucket.key]?.amount ?? 0,
    count: ageing?.[bucket.key]?.count,
  }));

  // The only period-over-period figure the API actually reports. Every other
  // card shows a real sub-figure instead of an invented percentage.
  const payrollDelta =
    data.payroll?.previousAmount && data.payroll.difference !== null
      ? { percent: Math.round((data.payroll.difference / data.payroll.previousAmount) * 100) }
      : undefined;

  if (layout !== 'desktop') {
    return (
      <CompactDashboard
        data={data}
        segments={segments}
        verticalName={verticalName}
        payrollDelta={payrollDelta}
        tablet={layout === 'tablet'}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Where the money is, right now"
        actions={
          can('report:read') ? (
            <Link className="btn-secondary" to="/reports">
              Reports
            </Link>
          ) : null
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card variant="hero" className="p-6 sm:col-span-2">
              <p className="stat-label text-brand-100">Total payables</p>
              <p className="mt-2 text-4xl font-semibold tracking-tight tabular">
                {money.format(data.totalPayables)}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="delta-on-dark">
                  {money.compact(data.payments.readyForPayment)} ready to pay
                </span>
                {can('obligation:read') ? (
                  <Link className="btn-accent" to="/payments">
                    <SendHorizontal className="h-4 w-4" aria-hidden="true" />
                    Payment queue
                  </Link>
                ) : null}
              </div>
              {data.payrollHidden ? (
                <p className="mt-4 text-xs text-brand-100">
                  Excludes payroll — your role does not include payroll visibility.
                </p>
              ) : null}
            </Card>

            <StatCard
              label="Unreconciled"
              value={money.compact(data.payments.unreconciled)}
              icon={RefreshCcw}
              tone={data.payments.unreconciled > 0 ? 'warning' : 'default'}
              sub={
                <Link className="text-brand-700" to="/reconciliation">
                  Reconcile
                </Link>
              }
            />
          </div>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-title mb-0">Invoices</h2>
              {can('invoice:read') ? (
                <Link className="text-sm text-brand-700" to="/invoices">
                  View all
                </Link>
              ) : null}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Received" value={data.invoices.received} icon={FileText} />
              <StatCard
                label="Pending review"
                value={data.invoices.pendingReview}
                icon={FileSearch}
                tone={data.invoices.pendingReview > 0 ? 'warning' : 'default'}
                sub={
                  <Link className="text-brand-700" to="/invoices/review">
                    Open review queue
                  </Link>
                }
              />
              <StatCard
                label="Pending approval"
                value={data.invoices.pendingApproval}
                icon={CircleCheck}
                sub={money.compact(data.invoices.pendingApprovalAmount)}
                tone={data.invoices.pendingApproval > 0 ? 'warning' : 'default'}
              />
              {data.invoices.accountingVerification > 0 ? (
                <StatCard
                  label="With accounting"
                  value={data.invoices.accountingVerification}
                  icon={Calculator}
                  sub={money.compact(data.invoices.accountingVerificationAmount)}
                  tone="warning"
                />
              ) : null}
              {data.invoices.treasuryApproval > 0 ? (
                <StatCard
                  label="With Treasury"
                  value={data.invoices.treasuryApproval}
                  icon={Gavel}
                  sub={money.compact(data.invoices.treasuryApprovalAmount)}
                  tone="warning"
                />
              ) : null}
              <StatCard
                label="Approved / unpaid"
                value={data.invoices.approvedUnpaid}
                icon={Wallet}
                sub={money.compact(data.invoices.approvedUnpaidAmount)}
              />
              {data.invoices.overdue > 0 ? (
                <StatCard
                  label="Overdue"
                  value={data.invoices.overdue}
                  icon={TriangleAlert}
                  sub={`${money.compact(data.invoices.overdueAmount)} past due date`}
                  tone="danger"
                />
              ) : null}
            </div>
          </section>

          {data.payroll ? (
            <section>
              <h2 className="section-title">Payroll</h2>
              <Card className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        className="text-lg font-semibold text-brand-800"
                        to={`/payroll/${data.payroll.batchId}`}
                      >
                        {data.payroll.label}
                      </Link>
                      <StatusBadge status={data.payroll.status} />
                    </div>
                    <p className="mt-1 text-sm text-ink-500">
                      {data.payroll.employeeCount.toLocaleString('en-IN')} employees
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold tabular">
                      {money.format(data.payroll.amount)}
                    </p>
                    {payrollDelta ? (
                      <span
                        className={`mt-1 ${payrollDelta.percent >= 0 ? 'delta-warn' : 'delta-up'}`}
                        title="Compared with the previous month"
                      >
                        {payrollDelta.percent >= 0 ? '+' : '−'}
                        {Math.abs(payrollDelta.percent)}% vs last month
                      </span>
                    ) : null}
                  </div>
                </div>
              </Card>
            </section>
          ) : null}

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="section-title mb-0">Payments</h2>
              {can('obligation:read') ? (
                <Link className="text-sm text-brand-700" to="/payments">
                  Payment queue
                </Link>
              ) : null}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                label="Ready for payment"
                value={money.compact(data.payments.readyForPayment)}
                icon={SendHorizontal}
              />
              <StatCard
                label="Batched / in flight"
                value={money.compact(data.payments.batched)}
                icon={Layers}
              />
              <StatCard
                label="Reconciled today"
                value={money.compact(data.payments.reconciledToday)}
                icon={CircleCheck}
                tone="success"
              />
            </div>
          </section>
        </div>

        <div className="space-y-6">
          {can('payable:read') ? (
            <Card>
              <div className="panel-head">
                <h2 className="font-semibold text-ink-900">Payables ageing</h2>
                <Link className="text-sm text-brand-700" to="/payables">
                  View all
                </Link>
              </div>
              <Donut segments={segments} caption="open" emptyLabel="Nothing outstanding" />
            </Card>
          ) : null}

          {/*
            Vertical-wise outstanding, which is the question the group CFO asks
            first and the reason the hierarchy exists at all. Gross, matching
            the payables figures above rather than the cash block below.
          */}
          {can('vertical:read') && data.outstandingByVertical.length ? (
            <Card>
              <div className="panel-head">
                <h2 className="font-semibold text-ink-900">Outstanding by vertical</h2>
              </div>
              <div className="divide-y divide-ink-100">
                {data.outstandingByVertical.map((row) => (
                  <Row
                    key={row.id ?? 'unassigned'}
                    label={`${verticalName(row.id) ?? 'Unassigned'} · ${row.count} invoice${
                      row.count === 1 ? '' : 's'
                    }`}
                    value={row.amount}
                  />
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <div className="panel-head">
              <h2 className="font-semibold text-ink-900">Cash visibility</h2>
            </div>
            <div className="divide-y divide-ink-100">
              <Row label="Current bank balance" value={data.cash.bankBalance} />
              <Row
                label="Approved vendor payables"
                value={data.cash.approvedVendorPayables}
                negative
              />
              {!data.cash.payrollExcluded ? (
                <Row label="Approved payroll" value={data.cash.approvedPayroll} negative />
              ) : null}
              <div className="flex items-center justify-between px-5 py-4">
                <span className="font-semibold text-ink-900">Known upcoming outflow</span>
                <Money minor={data.cash.knownUpcomingOutflow} className="text-lg font-semibold" />
              </div>
            </div>
            {data.cash.payrollExcluded ? (
              <p className="px-5 pb-4 text-xs text-ink-500">
                Payroll is excluded from this figure because your role does not include payroll
                access.
              </p>
            ) : null}
          </Card>
        </div>
      </div>
    </>
  );
}

type Summary = Awaited<ReturnType<typeof api.dashboard.summary>>;

/** Where each invoice row on the home screen opens. */
const INVOICE_STAGE_LINKS: Record<string, string> = {
  received: '/invoices',
  review: '/invoices/review',
  approval: '/invoices?view=PENDING_APPROVAL',
  accounting: '/invoices?view=ACCOUNTING',
  treasury: '/invoices?view=TREASURY',
  unpaid: '/invoices?view=APPROVED',
  overdue: '/invoices?view=OVERDUE',
};

/**
 * The home screen on a phone or tablet: the same answers as the desktop
 * dashboard, compacted. What we owe and what is unreconciled first, two up;
 * then where the money is in the payment pipeline; then invoices as a strip
 * that scrolls sideways; then payroll, ageing, verticals and cash.
 */
function CompactDashboard({
  data,
  segments,
  verticalName,
  payrollDelta,
  tablet,
}: {
  data: Summary;
  segments: DonutSegment[];
  verticalName(id: string | null): string | null | undefined;
  payrollDelta?: { percent: number };
  tablet: boolean;
}) {
  const { can } = useAuth();
  const money = useMoney();

  const invoiceCards = [
    {
      key: 'received',
      label: 'Received',
      value: data.invoices.received,
      icon: FileText,
      tone: 'default' as const,
    },
    {
      key: 'review',
      label: 'Pending review',
      value: data.invoices.pendingReview,
      icon: FileSearch,
      tone: data.invoices.pendingReview > 0 ? ('warning' as const) : ('default' as const),
      sub: 'Open review queue',
    },
    {
      key: 'approval',
      label: 'Pending approval',
      value: data.invoices.pendingApproval,
      icon: CircleCheck,
      tone: data.invoices.pendingApproval > 0 ? ('warning' as const) : ('default' as const),
      sub: money.compact(data.invoices.pendingApprovalAmount),
    },
    ...(data.invoices.accountingVerification > 0
      ? [
          {
            key: 'accounting',
            label: 'With accounting',
            value: data.invoices.accountingVerification,
            icon: Calculator,
            tone: 'warning' as const,
            sub: money.compact(data.invoices.accountingVerificationAmount),
          },
        ]
      : []),
    ...(data.invoices.treasuryApproval > 0
      ? [
          {
            key: 'treasury',
            label: 'With Treasury',
            value: data.invoices.treasuryApproval,
            icon: Gavel,
            tone: 'warning' as const,
            sub: money.compact(data.invoices.treasuryApprovalAmount),
          },
        ]
      : []),
    {
      key: 'unpaid',
      label: 'Approved / unpaid',
      value: data.invoices.approvedUnpaid,
      icon: Wallet,
      tone: 'default' as const,
      sub: money.compact(data.invoices.approvedUnpaidAmount),
    },
    ...(data.invoices.overdue > 0
      ? [
          {
            key: 'overdue',
            label: 'Overdue',
            value: data.invoices.overdue,
            icon: TriangleAlert,
            tone: 'danger' as const,
            sub: `${money.compact(data.invoices.overdueAmount)} past due`,
          },
        ]
      : []),
  ];

  const toneText = { default: 'text-ink-900', warning: 'text-amber-700', danger: 'text-red-700' };
  const toneChip = {
    default: 'bg-brand-50 text-brand-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-700',
  };

  return (
    <div className="space-y-5">
      {/* The page's own title and its Reports shortcut, as on the desktop. */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-ink-900">Dashboard</h1>
        {can('report:read') ? (
          <Link className="btn-secondary px-3 py-1.5 text-xs" to="/reports">
            <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
            Reports
          </Link>
        ) : null}
      </div>

      {/* Row 1: what we owe, and what the bank has not been matched against. */}
      <div className="grid grid-cols-2 gap-3">
        <Card variant="hero" className="flex flex-col p-4">
          <p className="stat-label text-brand-100">Total payables</p>
          <p className="mt-1 text-xl font-semibold tracking-tight tabular sm:text-2xl">
            {money.compact(data.totalPayables)}
          </p>
          <p className="mb-3 text-[11px] tabular text-brand-100">
            {money.format(data.totalPayables)}
          </p>
          {/* The "ready to pay" chip and the queue button, as one. */}
          {can('obligation:read') ? (
            <Link
              to="/payments"
              className="mt-auto inline-flex items-center justify-between gap-2 rounded-full bg-peridot-500 px-3 py-1.5 text-xs font-semibold text-ink-900 hover:bg-peridot-600"
              aria-label={
                'Payment queue, ' + money.compact(data.payments.readyForPayment) + ' ready to pay'
              }
            >
              <span className="truncate">{money.compact(data.payments.readyForPayment)} ready</span>
              <SendHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            </Link>
          ) : (
            <span className="mt-3 text-xs text-brand-100">
              {money.compact(data.payments.readyForPayment)} ready to pay
            </span>
          )}
          {data.payrollHidden ? (
            <p className="mt-2 text-[10px] leading-tight text-brand-100">
              Excludes payroll — not in your role
            </p>
          ) : null}
        </Card>

        <Card className="flex flex-col p-4">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${
              data.payments.unreconciled > 0 ? toneChip.warning : toneChip.default
            }`}
          >
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
          </span>
          <p className="stat-label mt-2">Unreconciled</p>
          <p
            className={`text-xl font-semibold tabular sm:text-2xl ${
              data.payments.unreconciled > 0 ? toneText.warning : toneText.default
            }`}
          >
            {money.compact(data.payments.unreconciled)}
          </p>
          <Link className="mt-auto pt-2 text-xs font-medium text-brand-700" to="/reconciliation">
            Reconcile →
          </Link>
        </Card>
      </div>

      {/* Row 2: the payment pipeline, ready → in flight → reconciled. */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-ink-900">Payments</h2>
          {can('obligation:read') ? (
            <Link className="text-sm font-medium text-brand-700" to="/payments">
              Payment queue
            </Link>
          ) : null}
        </div>
        <dl className="grid grid-cols-3 divide-x divide-ink-100 text-center">
          <div className="px-1">
            <dt className="text-[11px] text-ink-500">Ready to pay</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular text-ink-900 sm:text-base">
              {money.compact(data.payments.readyForPayment)}
            </dd>
          </div>
          <div className="px-1">
            <dt className="text-[11px] text-ink-500">In flight</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular text-ink-900 sm:text-base">
              {money.compact(data.payments.batched)}
            </dd>
          </div>
          <div className="px-1">
            <dt className="text-[11px] text-ink-500">Reconciled today</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular text-emerald-700 sm:text-base">
              {money.compact(data.payments.reconciledToday)}
            </dd>
          </div>
        </dl>
      </Card>

      {/*
        Invoices: one card, a row per stage. Each row opens that stage's list,
        so the card is both the summary and the way in.
      */}
      <Card>
        <div className="panel-head">
          <h2 className="font-semibold text-ink-900">Invoices</h2>
          {can('invoice:read') ? (
            <Link className="text-sm text-brand-700" to="/invoices">
              View all
            </Link>
          ) : null}
        </div>
        <ul className={tablet ? 'grid grid-cols-2' : 'divide-y divide-ink-100'}>
          {invoiceCards.map((card) => {
            const body = (
              <>
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${toneChip[card.tone]}`}
                >
                  <card.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink-700">{card.label}</span>
                  {'sub' in card && card.sub ? (
                    <span className="block truncate text-xs text-ink-500">{card.sub}</span>
                  ) : null}
                </span>
                <span className={`text-lg font-semibold tabular ${toneText[card.tone]}`}>
                  {card.value}
                </span>
              </>
            );
            const row = 'flex items-center gap-3 px-4 py-3';
            return (
              <li
                key={card.key}
                className={tablet ? 'border-b border-ink-100 odd:border-r' : undefined}
              >
                {can('invoice:read') ? (
                  <Link to={INVOICE_STAGE_LINKS[card.key]!} className={`${row} hover:bg-ink-50`}>
                    {body}
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" aria-hidden="true" />
                  </Link>
                ) : (
                  <div className={row}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {data.payroll ? (
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="stat-label">Payroll</p>
              <Link
                className="block truncate font-semibold text-brand-800"
                to={`/payroll/${data.payroll.batchId}`}
              >
                {data.payroll.label}
              </Link>
              <p className="text-xs text-ink-500">
                {data.payroll.employeeCount.toLocaleString('en-IN')} employees
              </p>
            </div>
            <StatusBadge status={data.payroll.status} />
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-3">
            <p className="text-xl font-semibold tabular">{money.format(data.payroll.amount)}</p>
            {payrollDelta ? (
              <span className={payrollDelta.percent >= 0 ? 'delta-warn' : 'delta-up'}>
                {payrollDelta.percent >= 0 ? '+' : '−'}
                {Math.abs(payrollDelta.percent)}% vs last month
              </span>
            ) : null}
          </div>
        </Card>
      ) : null}

      <div className={tablet ? 'grid grid-cols-2 gap-4' : 'space-y-5'}>
        {can('payable:read') ? (
          <Card>
            <div className="panel-head">
              <h2 className="font-semibold text-ink-900">Payables ageing</h2>
              <Link className="text-sm text-brand-700" to="/payables">
                View all
              </Link>
            </div>
            <Donut segments={segments} caption="open" emptyLabel="Nothing outstanding" />
          </Card>
        ) : null}

        {can('vertical:read') && data.outstandingByVertical.length ? (
          <Card>
            <div className="panel-head">
              <h2 className="font-semibold text-ink-900">Outstanding by vertical</h2>
            </div>
            <div className="divide-y divide-ink-100">
              {data.outstandingByVertical.map((row) => (
                <Row
                  key={row.id ?? 'unassigned'}
                  label={`${verticalName(row.id) ?? 'Unassigned'} · ${row.count} invoice${
                    row.count === 1 ? '' : 's'
                  }`}
                  value={row.amount}
                />
              ))}
            </div>
          </Card>
        ) : null}
      </div>

      <Card>
        <div className="panel-head">
          <h2 className="font-semibold text-ink-900">Cash visibility</h2>
        </div>
        <div className="divide-y divide-ink-100">
          <Row label="Current bank balance" value={data.cash.bankBalance} />
          <Row label="Approved vendor payables" value={data.cash.approvedVendorPayables} negative />
          {!data.cash.payrollExcluded ? (
            <Row label="Approved payroll" value={data.cash.approvedPayroll} negative />
          ) : null}
          <div className="flex items-center justify-between px-5 py-4">
            <span className="font-semibold text-ink-900">Known upcoming outflow</span>
            <Money minor={data.cash.knownUpcomingOutflow} className="text-lg font-semibold" />
          </div>
        </div>
        {data.cash.payrollExcluded ? (
          <p className="px-5 pb-4 text-xs text-ink-500">
            Payroll is excluded from this figure because your role does not include payroll access.
          </p>
        ) : null}
      </Card>
    </div>
  );
}

function Row({ label, value, negative }: { label: string; value: number; negative?: boolean }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <span className="text-sm text-ink-600">{label}</span>
      <Money minor={value} className={negative ? 'text-ink-700' : 'text-emerald-700'} />
    </div>
  );
}
